// @flow
import { FormattedMessage, useIntl } from 'react-intl';
import { getCategoryLandingPageBaseUrl, getPriceGuideUrl, urlSafeTitle } from '../../utils/urls';
import classNames from 'classnames';
import Form from 'react-bootstrap/lib/Form';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import NoResults from '../../components/Pagination/NoResults';
import PageTitle from '../../components/PageTitle/PageTitle';
import { preventDefault } from '../../utils/preventDefault';
import React from 'react';
import SaveSearch from '../../components/SaveSearch/SaveSearch';
import SearchFacets from '../../components/SearchFacets/SearchFacets';
import SearchSort from '../../components/SearchResults/SearchSort';
import styles from './CategoryLandingPageHeader.module.scss';
import ZeroResultsContainer from '../../components/ZeroResults/ZeroResultsContainer';

type Props = $ReadOnly<{|
    archivedSearch: boolean,
    campaignId?: any,
    clearAllParamsExceptCategories: Function,
    deleteSavedSearch: Function,
    distance: any,
    distanceSelected: Function,
    excludedFacets: any,
    facetOptionChecked: Function,
    facetOptionExcluded: Function,
    facets: any[],
    isLandingPage: boolean,
    isLoading: boolean,
    pageLabel: string,
    pageName: string,
    pathname: string,
    queryParams: any,
    rangeEntered: Function,
    ranges: any,
    removeCountryAndDistance: Function,
    replaceCountry: Function,
    saveSearchCheckState: any,
    saveSearchState: any,
    selectedCreator: any,
    showLandingPageLinks: boolean,
    sort: string,
    sortOptions: any[],
    sortSelected: Function,
    submitSaveSearch: Function,
    taxonomyAllSelected: Function,
    taxonomyParentSelected: Function,
    taxonomySelected: Function,
    totalItemsFound: number,
    whiteLabel: boolean,
    width: number,
|}>;

const safeNameUrl = (unsafeName?: string = '') => {
    const cleanedName = unsafeName.toLowerCase().replace('&', '');
    return urlSafeTitle(cleanedName);
};

const CategoryLandingPageHeader = (props: Props) => {
    const {
        archivedSearch,
        campaignId,
        clearAllParamsExceptCategories,
        deleteSavedSearch,
        distance,
        distanceSelected,
        excludedFacets,
        facetOptionChecked,
        facetOptionExcluded,
        facets,
        isLandingPage,
        isLoading,
        pageLabel = 'All Categories',
        pageName,
        pathname,
        queryParams,
        rangeEntered,
        ranges,
        removeCountryAndDistance,
        replaceCountry,
        saveSearchCheckState,
        saveSearchState,
        selectedCreator,
        showLandingPageLinks,
        sort,
        sortOptions,
        sortSelected,
        submitSaveSearch,
        taxonomyAllSelected,
        taxonomyParentSelected,
        taxonomySelected,
        totalItemsFound = 0,
        whiteLabel,
        width,
    } = props;

    const { formatMessage } = useIntl();

    const showNewZeroResultsHeader = true;
    const displayBasedOnTotalItemsFound = totalItemsFound > 0;
    const resultCountMessage = `${totalItemsFound.toLocaleString()}`;
    const categoryLandingPageLabel = archivedSearch
        ? `${pageLabel} ${formatMessage({ id: 'price_guide_results' })}`
        : pageLabel;
    const seeResultsRoute = !archivedSearch
        ? getPriceGuideUrl(selectedCreator?.seoURL || safeNameUrl(selectedCreator?.name), selectedCreator?.id)
        : `${getCategoryLandingPageBaseUrl()}${selectedCreator?.seoURL}/${selectedCreator?.id}/`;
    const seeResultsMessage = !archivedSearch ? 'see_auction_price_results_for' : 'see_upcoming_category_items';
    const tooltipMessage = <FormattedMessage id="follow_search_tooltip" values={{ token: pageLabel }} />;
    const isCampaignPage = Boolean(campaignId);

    const resultsWrapperStyles = classNames(styles['results-wrapper'], {
        [styles.noResults]: showNewZeroResultsHeader && !totalItemsFound,
    });

    const loadNoResults = !isLoading ? (
        <div className={styles['header-container']}>
            {!totalItemsFound && showNewZeroResultsHeader ? (
                <ZeroResultsContainer
                    categoryPage
                    className={resultsWrapperStyles}
                    searchTerm={categoryLandingPageLabel}
                    creator={selectedCreator}
                    seeResultsRoute={seeResultsRoute}
                />
            ) : (
                <div className={styles['search-terms-container']}>
                    <div className={styles['title-count']}>
                        <PageTitle className={styles['search-term']} title={categoryLandingPageLabel} />
                        <span className={styles['results-count']}>
                            (
                            <FormattedMessage id="search_item_count_items" values={{ count: resultCountMessage }} />)
                        </span>
                    </div>
                    {displayBasedOnTotalItemsFound && (
                        <div className={styles['button-creator-wrapper']}>
                            {!isCampaignPage && (
                                <SaveSearch
                                    deleteSavedSearch={deleteSavedSearch}
                                    primary
                                    saveSearchCheckState={saveSearchCheckState}
                                    saveSearchState={saveSearchState}
                                    submitSaveSearch={submitSaveSearch}
                                    tooltipMessage={tooltipMessage}
                                />
                            )}
                            {selectedCreator.name && (
                                <div className={styles['auction-results-container']}>
                                    <Link route to={seeResultsRoute}>
                                        <FormattedMessage
                                            id={seeResultsMessage}
                                            values={{ token: selectedCreator.name }}
                                        />
                                    </Link>
                                </div>
                            )}
                        </div>
                    )}
                    {!displayBasedOnTotalItemsFound && !isCampaignPage && (
                        <NoResults>
                            <div className={styles['no-results']}>
                                <FormattedMessage id="next_treasure" />
                                <FormattedMessage
                                    id="no_items_available"
                                    values={{ title: categoryLandingPageLabel }}
                                />
                                <div className={styles['save-button']}>
                                    <SaveSearch
                                        deleteSavedSearch={deleteSavedSearch}
                                        primary
                                        saveSearchCheckState={saveSearchCheckState}
                                        saveSearchState={saveSearchState}
                                        submitSaveSearch={submitSaveSearch}
                                        tooltipMessage={tooltipMessage}
                                    />
                                </div>
                            </div>
                        </NoResults>
                    )}
                </div>
            )}
            <Form className={styles.controls} inline onSubmit={preventDefault}>
                <div className={styles['results-wrapper']}>
                    {displayBasedOnTotalItemsFound && (
                        <>
                            <SearchFacets
                                clearAllParamsExceptCategories={clearAllParamsExceptCategories}
                                distance={distance}
                                distanceSelected={distanceSelected}
                                excludedFacets={excludedFacets}
                                facetOptionChecked={facetOptionChecked}
                                facetOptionExcluded={facetOptionExcluded}
                                facets={facets}
                                isLandingPage={isLandingPage}
                                pageName={pageName}
                                pathname={pathname}
                                queryParams={queryParams}
                                rangeEntered={rangeEntered}
                                ranges={ranges}
                                removeCountryAndDistance={removeCountryAndDistance}
                                replaceCountry={replaceCountry}
                                showLandingPageLinks={showLandingPageLinks}
                                taxonomyAllSelected={taxonomyAllSelected}
                                taxonomyParentSelected={taxonomyParentSelected}
                                taxonomySelected={taxonomySelected}
                                totalItemsFound={totalItemsFound}
                                whiteLabel={whiteLabel}
                                width={width}
                            />
                            <SearchSort
                                archivedSearch={archivedSearch}
                                sortSelected={sortSelected}
                                sortOptions={sortOptions}
                                sort={sort}
                            />
                        </>
                    )}
                </div>
            </Form>
        </div>
    ) : null;

    return <>{loadNoResults}</>;
};

export default React.memo<Props>(CategoryLandingPageHeader, isEqual);
